import { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, TagsOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import { updateDataSetRequest } from "store/dataSets/actions";
import { fetchDataSetById } from "./dataSetApi";
import { buildToolError } from "./errorEnvelope";
import type { ToolPartProps } from "./types";

type TagDimension = "intent" | "trustworthiness" | "quality" | "state" | "sourceType";

const DIMENSIONS: TagDimension[] = ["intent", "trustworthiness", "quality", "state", "sourceType"];

// Allow-list of enum values per dimension (mirrors dataset-service
// DATA_SET_HEADER_METADATA and the frontend DatasetHeaderMetadata enums).
const ALLOWED: Record<TagDimension, string[]> = {
    intent: ["CUSTOMER_360", "PREDICTIVE_ANALYTICS", "GDPR_DELETION", "MACHINE_LEARNING", "REPORTING", "OPERATIONAL", "ARCHIVAL"],
    trustworthiness: ["VERIFIED", "UNVERIFIED", "GOLD_STANDARD", "SYSTEM_GENERATED", "USER_PROVIDED", "EXTERNAL_SOURCE"],
    quality: ["HIGH_FIDELITY", "STANDARD", "REQUIRES_REVIEW", "LOW_QUALITY", "UNCLEAN", "RAW"],
    state: ["PRODUCTION", "STAGING", "EXPERIMENTAL", "DEPRECATED", "DRAFT", "ARCHIVED"],
    sourceType: ["ZERO_PARTY_DATA", "FIRST_PARTY_DATA", "SECOND_PARTY_DATA", "THIRD_PARTY_DATA", "SYNTHETIC_DATA"],
};

interface TagEntry {
    dimension: TagDimension;
    value: string;
}

interface UpdateDataSetTagsInput {
    add?: TagEntry[];
    remove?: TagEntry[];
    description: string;
}

interface UpdateDataSetTagsOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    verified?: boolean;
    appliedChanges?: string[];
    unappliedChanges?: string[];
}

type Phase = "ready" | "denied" | "applying" | "applied" | "error";

type TagsMap = Partial<Record<TagDimension, string[]>>;

function normaliseEntry(e: TagEntry): TagEntry | null {
    if (!e || !e.dimension || !e.value) return null;
    const dimension = String(e.dimension) as TagDimension;
    if (!DIMENSIONS.includes(dimension)) return null;
    const value = String(e.value).trim().toUpperCase();
    if (!ALLOWED[dimension].includes(value)) return null;
    return { dimension, value };
}

// Clone the current tags map and apply add/remove. Returns the merged map plus
// the set of changes that actually altered the map (for the confirmation card).
function mergeTags(current: TagsMap, add: TagEntry[], remove: TagEntry[]): { merged: TagsMap; changes: string[] } {
    const merged: TagsMap = {};
    for (const dim of DIMENSIONS) {
        const existing = Array.isArray(current?.[dim]) ? [...(current[dim] as string[])] : [];
        merged[dim] = existing;
    }
    const changes: string[] = [];
    for (const e of add) {
        const list = merged[e.dimension] as string[];
        if (!list.includes(e.value)) {
            list.push(e.value);
            changes.push(`+ ${e.value} (${e.dimension})`);
        }
    }
    for (const e of remove) {
        const list = merged[e.dimension] as string[];
        const idx = list.indexOf(e.value);
        if (idx >= 0) {
            list.splice(idx, 1);
            changes.push(`- ${e.value} (${e.dimension})`);
        }
    }
    // Drop empty dimensions to keep the payload clean (matches unflattenTags).
    for (const dim of DIMENSIONS) {
        if ((merged[dim] as string[]).length === 0) delete merged[dim];
    }
    return { merged, changes };
}

export default function UpdateDataSetTagsTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<UpdateDataSetTagsInput, UpdateDataSetTagsOutput>) {
    const { dispatch, addToolOutput, businessAreaId, schemaId, tenantId, dataSet, mode, permission, updateDataSetResult } = helpers;

    const [phase, setPhase] = useState<Phase>("ready");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    const resultAtDispatchRef = useRef<unknown>(null);

    const validAdd = (input?.add || []).map(normaliseEntry).filter((e): e is TagEntry => e !== null);
    const validRemove = (input?.remove || []).map(normaliseEntry).filter((e): e is TagEntry => e !== null);
    const rejected = [...(input?.add || []), ...(input?.remove || [])]
        .filter((e) => normaliseEntry(e) === null)
        .map((e) => `${e?.value ?? "?"} (${e?.dimension ?? "?"})`);

    const currentTags: TagsMap = (dataSet?.metadata?.tags as TagsMap) || {};
    const { merged, changes } = mergeTags(currentTags, validAdd, validRemove);
    const nothingToDo = changes.length === 0;

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        initialisedRef.current = true;

        if (mode === "view" || !permission?.canEdit) {
            setPhase("denied");
            setDenyReason("You're in read-only mode for this data set, so I can't change its tags.");
            return;
        }
        if (!dataSet || !dataSet.dataSetId) {
            setPhase("denied");
            setDenyReason("No data set is loaded.");
            return;
        }
        if (validAdd.length === 0 && validRemove.length === 0) {
            setPhase("denied");
            setDenyReason(
                rejected.length > 0
                    ? `None of those are recognised tags: ${rejected.join(", ")}.`
                    : "No valid tags to add or remove were provided.",
            );
            return;
        }
    }, [state, mode, permission, dataSet, validAdd.length, validRemove.length, rejected]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied") {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataSetTags", toolCallId, output: { success: false, error: denyReason || "Cannot update tags." } });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "applying") return;
        if (!updateDataSetResult) return;
        if (updateDataSetResult === resultAtDispatchRef.current) return;
        if (updateDataSetResult.success === true) {
            (async () => {
                let refreshed: { metadata?: { tags?: TagsMap } } | null = null;
                let fetchErr: string | null = null;
                try {
                    if (tenantId && businessAreaId && schemaId && dataSet?.dataSetId) {
                        refreshed = (await fetchDataSetById(tenantId, businessAreaId, schemaId, dataSet.dataSetId)) as any;
                    }
                } catch (e: any) {
                    fetchErr = e?.message || "fetch error";
                }
                const applied: string[] = [];
                const unapplied: string[] = [];
                if (fetchErr) {
                    // The PUT succeeded; verification GET is best-effort. Trust the write.
                    applied.push(...changes);
                } else {
                    const serverTags = (refreshed?.metadata?.tags as TagsMap) || {};
                    const has = (dim: TagDimension, v: string) => Array.isArray(serverTags[dim]) && (serverTags[dim] as string[]).includes(v);
                    for (const e of validAdd) {
                        if (has(e.dimension, e.value)) applied.push(`+ ${e.value} (${e.dimension})`);
                        else unapplied.push(`add ${e.value} (${e.dimension}) did not apply`);
                    }
                    for (const e of validRemove) {
                        if (!has(e.dimension, e.value)) applied.push(`- ${e.value} (${e.dimension})`);
                        else unapplied.push(`remove ${e.value} (${e.dimension}) did not apply`);
                    }
                }
                setPhase("applied");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    addToolOutput({
                        tool: "updateDataSetTags",
                        toolCallId,
                        output: {
                            success: true,
                            verified: !fetchErr && unapplied.length === 0,
                            appliedChanges: applied,
                            unappliedChanges: unapplied,
                        },
                    });
                }
            })();
        } else if (updateDataSetResult.success === false) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "updateDataSetTags",
                    toolCallId,
                    output: buildToolError(updateDataSetResult, "Tag update failed."),
                });
            }
        }
    }, [updateDataSetResult, phase, changes, validAdd, validRemove, dataSet, toolCallId, addToolOutput, tenantId, businessAreaId, schemaId]);

    const handleApply = () => {
        if (!businessAreaId || !schemaId || !dataSet || nothingToDo) return;
        // The backend REPLACES metadata wholesale, so resend the existing rating
        // alongside the merged tags, otherwise the rating would be cleared.
        const updatedDataSet: Record<string, unknown> = {
            metadata: {
                rating: dataSet.metadata?.rating ?? 0,
                tags: merged,
            },
        };
        resultAtDispatchRef.current = updateDataSetResult;
        setPhase("applying");
        // source="assistant" suppresses the legacy redirect (PRE-2623 bug 4).
        dispatch(updateDataSetRequest(businessAreaId, schemaId, dataSet, updatedDataSet, "assistant"));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataSetTags", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <TagsOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Update tags</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{dataSet?.dataSetName || "(no data set)"}</div>
            {input?.description && <div className="mt-1 text-xs text-zinc-600">{input.description}</div>}

            {phase === "denied" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {phase !== "denied" && (
                <div className="mt-2 space-y-2">
                    {validAdd.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 text-xs">
                            <span className="text-zinc-500">Add:</span>
                            {validAdd.map((e) => (
                                <Tag key={`a-${e.dimension}-${e.value}`} color="green">{e.value}</Tag>
                            ))}
                        </div>
                    )}
                    {validRemove.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 text-xs">
                            <span className="text-zinc-500">Remove:</span>
                            {validRemove.map((e) => (
                                <Tag key={`r-${e.dimension}-${e.value}`} color="red">{e.value}</Tag>
                            ))}
                        </div>
                    )}
                    {rejected.length > 0 && (
                        <div className="text-[10px] text-amber-700">Ignored (not recognised tags): {rejected.join(", ")}</div>
                    )}
                    {nothingToDo && <div className="text-xs text-zinc-500">No changes — those tags already match the current state.</div>}
                </div>
            )}

            {phase === "applied" && output?.verified !== false && (output?.unappliedChanges?.length || 0) === 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Tags updated.
                </div>
            )}
            {phase === "applied" && (output?.verified === false || (output?.unappliedChanges?.length || 0) > 0) && (
                <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <div className="flex items-center gap-2 font-medium">
                        <WarningOutlined /> Some tag changes did not apply
                    </div>
                    <ul className="ml-4 mt-1 list-disc">
                        {(output?.unappliedChanges || []).map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
            )}
            {phase === "error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Tag update failed.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={handleCancel} className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={nothingToDo}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Apply
                    </button>
                </div>
            )}
            {phase === "applying" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Applying...
                </div>
            )}
        </div>
    );
}
